import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { APP_VERSION } from '../constants/release-notes';

/** How often an open, visible app asks whether a newer version is out (it also asks on opening, on coming back to it and on coming back online). */
const CHECK_EVERY_MS = 5 * 60 * 1000;
/** A check on focus or reconnect closer than this to the last one is skipped. */
const MIN_GAP_MS = 60 * 1000;
/** Just after the app opens, before anyone has touched it, a waiting update is applied without asking. */
const OPENING_GRACE_MS = 10 * 1000;
/** How long a check waits for the service worker before asking the server itself. */
const SW_TIMEOUT_MS = 20 * 1000;
/** How long the Update button waits for the worker to switch to a version it already holds. */
const SWITCH_TIMEOUT_MS = 5 * 1000;
/** sessionStorage: an update in progress, carried across the reload. */
const UPDATE_KEY = 'link-update';
/** sessionStorage: when the app last reloaded because a page's code was missing. */
const CHUNK_KEY = 'link-chunk-reload';

interface PendingUpdate {
  /** The build the button was pressed on. */
  from: string;
  /** What's new version of that build. */
  version: string;
  at: number;
}

/** main-XXXX.js out of a script src or a whole index.html; '' when there is none (the dev server). */
export function buildOf(text: string): string {
  return (String(text || '').match(/main-[A-Za-z0-9]+\.js/) || [''])[0];
}

/** The build this page runs: the hashed main bundle index.html loaded. */
export function runningBuild(doc: Document = document): string {
  return buildOf(doc.querySelector<HTMLScriptElement>('script[src*="main-"]')?.getAttribute('src') || '');
}

/** A route's code that could not be fetched — after a deploy the old files are gone from the server. */
export function isMissingChunk(error: unknown): boolean {
  const message = String((error as { message?: unknown })?.message ?? error ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk [\w-]+ failed/i.test(message);
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timed out')), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

function readSession(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch { /* private mode: the update still happens, only the note after it is lost */ }
}

/**
 * A hard reload: unregister this app's service worker (ngsw-worker.js — the push worker,
 * firebase-messaging-sw.js, is left alone), drop its caches and load straight from the server.
 * The app registers its worker again on its own.
 */
async function reloadFromServer(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations?.()) || [];
    await Promise.all(
      registrations
        .filter((r) => [r.active, r.waiting, r.installing].some((w) => !!w?.scriptURL?.endsWith('/ngsw-worker.js')))
        .map((r) => r.unregister()),
    );
  } catch { /* reload anyway */ }
  try {
    const keys = (await caches?.keys?.()) || [];
    await Promise.all(keys.filter((k) => k.startsWith('ngsw:')).map((k) => caches.delete(k)));
  } catch { /* reload anyway */ }
  location.reload();
}

/**
 * New versions of Link (30 Oct 2026, founder). The app no longer reloads itself in the middle of
 * work: when a newer version is out, an Update button shows — next to the logo once signed in, as
 * the Link icon at the top left of the sign-in screen, floating at the top of other pages without
 * the header — and one tap loads it, in the installed app on a phone or a desktop and in a plain
 * browser tab alike; then the button is gone. Only in the first seconds after the app opens, before
 * anyone has touched it, is a waiting update applied straight away (once — never in a loop).
 *
 * A newer version is spotted two ways: the Angular service worker (`checkForUpdate`, VERSION_READY)
 * and index.html fetched past every cache (its hashed main bundle differs from this page's), so a
 * tab without a service worker learns of it too. The button is a quick hard reload: when the worker
 * already holds the new version it switches to it and reloads; otherwise the worker and its caches
 * are cleared and the page loads straight from the server.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly sw = inject(SwUpdate);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly snackBar = inject(MatSnackBar);

  /** A newer Link is out: the Update button shows. */
  readonly available = signal(false);
  /** The Update button was pressed; the new version is loading. */
  readonly updating = signal(false);
  /** Update buttons a page shows itself (the header's, the sign-in screen's); with none (chat on a phone) the app shows a floating one. */
  private readonly pageButtons = signal(0);
  readonly needsFloatingButton = computed(() => this.available() && this.pageButtons() === 0);

  private readonly build = runningBuild();
  private readonly openedAt = Date.now();
  private touched = false;
  private lastCheck = 0;
  private swReady = false;
  private started = false;
  /** This page is the reload of an update that brought the same version back: offer the button, never reload by itself. */
  private cameBackOld = false;

  /** Called once by the app root. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.reportAfterReload();

    const touch = () => { this.touched = true; };
    window.addEventListener('pointerdown', touch, { capture: true, once: true });
    window.addEventListener('keydown', touch, { capture: true, once: true });

    if (this.sw.isEnabled) {
      this.sw.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          this.swReady = true;
          this.offer();
        }
      });
      // The worker cannot serve this copy any more (its files are gone): only a reload helps.
      this.sw.unrecoverable.subscribe(() => this.offer());
    }
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError && isMissingChunk(event.error)) this.recoverMissingChunk(event.url);
    });

    this.zone.runOutsideAngular(() => {
      setTimeout(() => void this.check(true), 5000);
      setInterval(() => {
        if (document.visibilityState === 'visible') void this.check(true);
      }, CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.check();
      });
      window.addEventListener('online', () => void this.check());
    });
  }

  /** A page's own Update button is on screen until the returned function is called. */
  registerPageButton(): () => void {
    this.pageButtons.update((n) => n + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.pageButtons.update((n) => Math.max(0, n - 1));
    };
  }

  /** Is a newer version out? Asks the service worker, then index.html straight from the server. */
  async check(scheduled = false): Promise<void> {
    if (this.available() || this.updating()) return;
    const now = Date.now();
    if (!scheduled && now - this.lastCheck < MIN_GAP_MS) return;
    this.lastCheck = now;
    if (this.sw.isEnabled && navigator.serviceWorker?.controller) {
      try {
        await withTimeout(this.sw.checkForUpdate(), SW_TIMEOUT_MS);
      } catch { /* the server check below still runs */ }
      if (this.available() || this.updating()) return;
    }
    const latest = await this.latestBuild();
    if (latest && this.build && latest !== this.build) this.offer();
  }

  /** The Update button: a quick hard reload into the newest version. */
  async apply(): Promise<void> {
    if (this.updating()) return;
    this.zone.run(() => this.updating.set(true));
    const pending: PendingUpdate = { from: this.build, version: APP_VERSION, at: Date.now() };
    writeSession(UPDATE_KEY, JSON.stringify(pending));
    // The worker already holds the new version: switch to it, and the reload is instant
    // (unless switching already brought the old copy back once — then the server decides).
    if (this.swReady && !this.cameBackOld && navigator.serviceWorker?.controller) {
      const switched = await withTimeout(this.sw.activateUpdate(), SWITCH_TIMEOUT_MS).catch(() => false);
      if (switched) {
        location.reload();
        return;
      }
    }
    // Anything else goes straight to the server — nothing waits on the worker downloading first.
    await reloadFromServer();
  }

  /** The main bundle the server hands out now, past the service worker and every cache. */
  private async latestBuild(): Promise<string> {
    try {
      const res = await fetch(`/index.html?ngsw-bypass=true&t=${Date.now()}`, { cache: 'no-store' });
      return res.ok ? buildOf(await res.text()) : '';
    } catch {
      return '';
    }
  }

  private offer(): void {
    this.zone.run(() => {
      if (this.updating() || this.available()) return;
      // Just opened and nobody has touched it yet: nothing to lose, so load the new version straight away.
      if (!this.touched && !this.cameBackOld && Date.now() - this.openedAt < OPENING_GRACE_MS) {
        void this.apply();
        return;
      }
      this.available.set(true);
    });
  }

  /** After the button's reload: say it worked — or, when the same version came back, leave the button to the person. */
  private reportAfterReload(): void {
    let pending: PendingUpdate | null = null;
    try { pending = JSON.parse(readSession(UPDATE_KEY) || 'null'); } catch { pending = null; }
    if (!pending) return;
    writeSession(UPDATE_KEY, null);
    if (Date.now() - Number(pending.at || 0) > 2 * 60 * 1000) return;
    if (this.build && pending.from !== this.build) {
      const previous = pending.version;
      setTimeout(() => this.zone.run(() => this.toastUpdated(previous)), 1500);
    } else {
      this.cameBackOld = true;
    }
  }

  private toastUpdated(previousVersion: string): void {
    const news = !!previousVersion && previousVersion !== APP_VERSION;
    const ref = this.snackBar.open(
      news ? 'Link is up to date — there is something new' : 'Link is up to date',
      news ? "What's new" : 'OK',
      { duration: 5000, verticalPosition: 'top', horizontalPosition: 'center' },
    );
    if (news) ref.onAction().subscribe(() => void this.router.navigate(['/whats-new']));
  }

  /** A page's code is gone from the server (a newer version replaced it): open that page again from a fresh load, at most once a minute. */
  private recoverMissingChunk(url: string): void {
    if (navigator.onLine === false) return;
    const last = Number(readSession(CHUNK_KEY) || 0);
    if (Date.now() - last < 60 * 1000) {
      this.zone.run(() => this.available.set(true));
      return;
    }
    writeSession(CHUNK_KEY, String(Date.now()));
    // The route lives in the hash (HashLocationStrategy): put the page asked for there, then load fresh.
    history.replaceState(history.state, '', '#' + url);
    location.reload();
  }
}
